import React, { useState, useEffect } from "react";
import config from "../../config/config";

const NavigationManager = () => {
  const [navigationData, setNavigationData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    icon: "",
    parent_id: "",
    order_index: 0,
    is_active: true,
    has_page: true,
  });

  // Icon options for navigation items
  const iconOptions = [
    { value: "", label: "No Icon" },
    { value: "fas fa-home", label: "Home" },
    { value: "fas fa-cog", label: "Manufacturing" },
    { value: "fas fa-tools", label: "Tools" },
    { value: "fas fa-shopping-cart", label: "Sourcing" },
    { value: "fas fa-briefcase", label: "Services" },
    { value: "fas fa-users", label: "Careers" },
    { value: "fas fa-file-alt", label: "Projects" },
    { value: "fas fa-info-circle", label: "About Us" },
    { value: "fas fa-envelope", label: "Contact" },
    { value: "fas fa-wrench", label: "Building Hardware" },
    { value: "fas fa-shield-alt", label: "Fencing" },
    { value: "fas fa-paw", label: "Animal Handling" },
    { value: "fas fa-drafting-compass", label: "Angle Brackets" },
    { value: "fas fa-link", label: "Timber Connector" },
    { value: "fas fa-building", label: "Scaffolding" },
    { value: "fas fa-car", label: "Automobile" },
    { value: "fas fa-bolt", label: "Fasteners" },
  ];

  // Fetch navigation on component mount
  useEffect(() => {
    fetchNavigation();
  }, []);

  // Fetch navigation data
  const fetchNavigation = async () => {
    try {
      setLoading(true);
      const baseApiUrl = config.getApiUrl("");

      const response = await fetch(`${baseApiUrl}get_navigation.php`);
      const data = await response.json();

      if (data.success) {
        setNavigationData(data.navigation);
      }
    } catch (error) {
      console.error("Error fetching navigation:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Auto-generate slug from title
    if (name === "title") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const url = editingItem
        ? config.getAdminApiUrl("update_navigation.php")
        : config.getAdminApiUrl("create_navigation.php");

      const requestData = editingItem
        ? { ...formData, id: editingItem.id }
        : formData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchNavigation();
        resetForm();
        setShowAddModal(false);
        alert(
          editingItem ? "Navigation item updated!" : "Navigation item created!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving navigation item:", error);
      alert("Error saving navigation item");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      icon: "",
      parent_id: "",
      order_index: 0,
      is_active: true,
      has_page: true,
    });
    setEditingItem(null);
  };

  // Handle edit navigation item
  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      slug: item.slug,
      icon: item.icon || "",
      parent_id: item.parent_id || "",
      order_index: item.order_index || 0,
      is_active: item.is_active,
      has_page: item.has_page,
    });
    setEditingItem(item);
    setShowAddModal(true);
  };

  // Handle delete navigation item
  const handleDelete = async (itemId) => {
    if (
      !window.confirm("Are you sure you want to delete this navigation item?")
    )
      return;

    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("delete_navigation.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: itemId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchNavigation();
        alert("Navigation item deleted!");
      } else {
        alert("Error deleting item: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting navigation item:", error);
      alert("Error deleting navigation item");
    } finally {
      setLoading(false);
    }
  };

  // Flatten navigation for parent selection
  const flattenNavigation = (items, level = 0) => {
    let result = [];
    items.forEach((item) => {
      result.push({
        id: item.id,
        title: "—".repeat(level) + " " + item.title,
        level: level,
      });
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenNavigation(item.children, level + 1));
      }
    });
    return result;
  };

  // Render navigation tree
  const renderNavigationTree = (items, level = 0) => {
    return items.map((item) => (
      <div key={item.id} className={`nav-tree-item level-${level}`}>
        <div className="nav-item-content">
          <div className="nav-item-info">
            {item.icon && <i className={`${item.icon} me-2`}></i>}
            <span className="nav-title">{item.title}</span>
            <span className="nav-slug">({item.slug})</span>
            <span
              className={`nav-status ${item.is_active ? "active" : "inactive"}`}
            >
              {item.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="nav-item-actions">
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handleEdit(item)}
              title="Edit"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(item.id)}
              title="Delete"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
        {item.children && item.children.length > 0 && (
          <div className="nav-children">
            {renderNavigationTree(item.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const flatNavigationData = flattenNavigation(navigationData);

  return (
    <div className="navigation-manager">
      <div className="manager-header">
        <h2>Navigation Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <i className="fas fa-plus me-2"></i>
          Add Navigation Item
        </button>
      </div>

      {/* Navigation Tree */}
      <div className="manager-content">
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="navigation-tree">
            <h3>Navigation Structure</h3>
            <div className="nav-tree">
              {navigationData.length > 0 ? (
                renderNavigationTree(navigationData)
              ) : (
                <div className="no-data">No navigation items found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {editingItem ? "Edit Navigation Item" : "Add Navigation Item"}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Navigation title"
                  />
                </div>

                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    placeholder="url-slug"
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <select
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                  >
                    {iconOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Parent Item</label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Top Level Item</option>
                    {flatNavigationData
                      .filter(
                        (item) => !editingItem || item.id !== editingItem.id
                      )
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Order Index</label>
                  <input
                    type="number"
                    name="order_index"
                    value={formData.order_index}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="has_page"
                      checked={formData.has_page}
                      onChange={handleInputChange}
                    />
                    <span>Has Dedicated Page</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingItem ? "Update Item" : "Create Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationManager;
